import React, { useContext } from 'react';
import styled from 'styled-components';
import { breakpoint, colors, sizes } from 'variables';
import { em } from 'modules/helpers/style';
import { toHtmlContent } from 'modules/helpers/strings';
import type { StoreParcelShopResult } from 'autogen/swagger/all';
import { BorderRadiusMedium, ColorNordicGrey4, Spacing16 } from 'autogen/design-tokens/tokens';
import { Body } from 'components/generic/typography';
import OpeningHoursDefault from 'components/OpeningHours';
import { FindStoreContext } from '../findStore';
import { useTranslation } from 'react-i18next';
import { FewInStock, InStock, MoreAboutStore, SoldOut } from 'autogen/translation-keys/trans-website-myprofile-store';

const Wrapper = styled.div`
    display: flex;
    background-color: ${ColorNordicGrey4};
    border-radius: ${BorderRadiusMedium};
    margin-bottom: 10px;
    flex-direction: column;
    padding: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        padding: 20px;
    }
`;

const Content = styled.div`
    display: flex;
`;

const LeftContent = styled.div`
    flex: 1 1 auto;
    min-width: 1px;
`;
const RightContent = styled.div`
    flex: 1 0 120px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-width: 1px;
    margin-left: 10px;
    max-width: 40%;

    ${breakpoint.up(sizes.md)} {
        max-width: none;
        margin-left: 50px;
    }
`;

const RightContentInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: flex-end;
    justify-content: flex-end;
    font-weight: bold;
`;

const Tags = styled.div`
    margin-bottom: 20px;
    font-weight: bold;
`;

const StoreInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 1px;

    ${breakpoint.up(sizes.md)} {
        flex-direction: row;
        justify-content: space-between;
    }
`;

const Address = styled.div`
    display: flex;
    flex-direction: column;

    ${breakpoint.up(sizes.md)} {
        min-width: ${em('250px')};
    }
`;

const OpeningHours = styled(OpeningHoursDefault)`
    margin-top: 20px;

    ${breakpoint.up(sizes.md)} {
        margin-top: 0px;
    }
`;

const Link = styled.a`
    text-align: right;
    text-decoration: underline;
    margin-top: 20px;
`;

const StockStatusWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    text-align: right;
`;
const StockIndicator = styled.div<{ color: string }>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ color }): string => color};
    margin-right: 5px;
    min-width: 12px;
`;

const StockText = styled.span`
    white-space: nowrap;
`;

function getStockSettings(productStockStatus: StoreParcelShopResult['productStockStatus']): {
    color: string;
    text: string;
} {
    const { t } = useTranslation();

    switch (productStockStatus) {
        case 'IsInStock':
            return { color: colors.success, text: t(InStock) };
        case 'FewInStock':
            return { color: colors.warning, text: t(FewInStock) };
        default:
            return { color: colors.error, text: t(SoldOut) };
    }
}

interface IFindStoreItem {
    parcelShop: StoreParcelShopResult;
}

export default (props: IFindStoreItem): JSX.Element => {
    const { parcelShop } = props;
    const { addressLines, distance, openingHours, tags, productStockStatus, url } = parcelShop;
    const { showStock } = useContext(FindStoreContext);
    const { t } = useTranslation();

    const stockStatus = productStockStatus ? getStockSettings(productStockStatus) : null;
    const hasTags = !!tags.length;

    return (
        <Wrapper>
            <Content>
                <LeftContent>
                    {hasTags && (
                        <Tags>
                            {tags.map(
                                (tag, i): JSX.Element => (
                                    <Body as="div" key={i}>
                                        {tag.text}
                                    </Body>
                                )
                            )}
                        </Tags>
                    )}
                    <StoreInfo>
                        {addressLines && (
                            <Address>
                                {addressLines.map(
                                    (addressLine): JSX.Element => (
                                        <Body as="span" key={addressLine}>
                                            {toHtmlContent(addressLine)}
                                        </Body>
                                    )
                                )}
                            </Address>
                        )}
                        <OpeningHours openingHours={openingHours} />
                    </StoreInfo>
                </LeftContent>
                <RightContent>
                    <RightContentInfo>
                        {distance && (
                            <Body as="div" size="M" strong>
                                {distance}
                            </Body>
                        )}
                        {showStock && (
                            <StockStatusWrapper>
                                <StockIndicator color={stockStatus.color} />
                                <StockText>{stockStatus.text}</StockText>
                            </StockStatusWrapper>
                        )}
                    </RightContentInfo>
                    {url && <Link href={url}>{t(MoreAboutStore)}</Link>}
                </RightContent>
            </Content>
        </Wrapper>
    );
};
